import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Trophy, Users, Zap, ChevronRight, Target, TrendingUp, Clock, Flame, Trash2 } from 'lucide-react'
import { bracketApi, leaguesApi, api } from '@/services/api'
import toast from 'react-hot-toast'
import { useAuthStore } from '@/stores/authStore'
import { useSportStore } from '@/stores/sportStore'
import LiveFeed from '@/components/leagues/LiveFeed'
import { LivePulse, StreakBadge } from '@/components/ui/ArcadeEffects'
import clsx from 'clsx'

/* ── Glass helper ─────────────────────────────────────────── */
const Glass: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={clsx('backdrop-blur-xl bg-white/[0.04] border border-white/[0.08] rounded-2xl', className)}>
    {children}
  </div>
)

/* ── Types ────────────────────────────────────────────────── */
interface BracketSummary {
  id: string
  name: string
  tournamentId: string
  totalPicks: number
  isSubmitted: boolean
  score?: number
  rank?: number
  createdAt: string
}

interface LeagueSummary {
  id: string
  name: string
  memberCount: number
  myRank?: number
  myScore?: number
}

/* ── Component ────────────────────────────────────────────── */
const DashboardPage: React.FC = () => {
  const { user } = useAuthStore()
  const { setSport } = useSportStore()
  const [brackets, setBrackets] = useState<BracketSummary[]>([])
  const [leagues, setLeagues] = useState<LeagueSummary[]>([])
  const [simStatus, setSimStatus] = useState<{ gamesCompleted: number; totalGames: number; currentRound: number | null } | null>(null)
  const [displayName, setDisplayName] = useState<string>('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      try {
        const [bracketsRes, leaguesRes, simRes, profileRes] = await Promise.allSettled([
          bracketApi.getUserBrackets(),
          leaguesApi.list(),
          api.get<any>('/sim/status'),
          api.get<any>('/profile'),
        ])

        if (bracketsRes.status === 'fulfilled') {
          const raw = bracketsRes.value
          setBrackets((Array.isArray(raw) ? raw : []).map((b: any) => ({
            id: b.id,
            name: b.name || 'My Bracket',
            tournamentId: b.tournamentId || b.tournament_id,
            totalPicks: b.totalPicks ?? b.total_picks ?? 0,
            isSubmitted: b.isSubmitted ?? b.is_submitted ?? false,
            score: b.score ?? b.current_score,
            rank: b.rank,
            createdAt: b.createdAt || b.created_at,
          })))
        }

        if (leaguesRes.status === 'fulfilled') {
          const raw = leaguesRes.value
          const list = Array.isArray(raw) ? raw : raw?.leagues || []
          setLeagues(list.map((l: any) => ({
            id: l.id,
            name: l.name,
            memberCount: l.memberCount ?? l.member_count ?? 0,
            myRank: l.myRank ?? l.my_rank,
            myScore: l.myScore ?? l.my_score,
          })))
        }

        if (simRes.status === 'fulfilled') {
          setSimStatus(simRes.value)
        }

        if (profileRes.status === 'fulfilled') {
          const p = (profileRes.value as any)?.profile || profileRes.value
          setDisplayName(p?.displayName || p?.display_name || p?.username || '')
        }
      } catch (e) {
        console.warn('Dashboard load error', e)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const totalPicks = brackets.reduce((sum, b) => sum + (b.totalPicks || 0), 0)
  const bestScore = brackets.reduce((best, b) => Math.max(best, b.score || 0), 0)
  const bestRank = brackets.reduce((best: number | null, b) => {
    if (!b.rank) return best
    return best === null ? b.rank : Math.min(best, b.rank)
  }, null as number | null)

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Clock className="h-6 w-6 text-white/20 animate-pulse" />
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
      {/* Countdown banner */}
      {(() => {
        const tipOff = new Date('2026-03-17T12:00:00-04:00') // First Four
        const daysLeft = Math.max(0, Math.ceil((tipOff.getTime() - Date.now()) / 86400000))
        if (daysLeft <= 0 || daysLeft > 60) return null
        return (
          <div className="bg-gradient-to-r from-orange-500/10 via-red-500/10 to-orange-500/10 border border-orange-500/20 rounded-2xl p-4 flex items-center justify-between animate-border-glow">
            <div className="flex items-center gap-3">
              <span className="text-3xl animate-fire">🏀</span>
              <div>
                <p className="text-sm font-bold text-fire">March Madness 2026</p>
                <p className="text-xs text-white/40">First Four tips off in {daysLeft} days</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-3xl font-black text-fire tabular-nums">{daysLeft}</p>
              <p className="text-[10px] text-white/30 uppercase tracking-wider">days to go</p>
            </div>
          </div>
        )
      })()}


      {/* Sport Switcher */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
        {/* World Cup Blitz — Featured */}
        <Link to="/wc/knockout" className="group h-full" onClick={() => setSport('wc')}>
          <Glass className="p-5 h-full flex flex-col border-emerald-500/20 bg-gradient-to-br from-emerald-500/10 to-green-700/5 hover:border-emerald-400/30 transition-all duration-300">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-2xl">⚽</span>
                <span className="text-xs font-bold text-emerald-400 bg-emerald-500/20 px-2 py-0.5 rounded">NEW</span>
              </div>
              <ChevronRight className="h-4 w-4 text-emerald-400 group-hover:translate-x-1 transition-transform" />
            </div>
            <h3 className="text-lg font-bold text-white mb-1">World Cup Blitz 2026</h3>
            <p className="text-xs text-white/40 mb-3">32-team knockout bracket · Pick winners · Crown a champion</p>
            <div className="flex items-center gap-3 mt-auto">
              {(() => {
                try {
                  const ko = Object.keys(JSON.parse(localStorage.getItem('wcb-ko-picks') || '{}')).length;
                  if (ko === 0) return <span className="text-xs text-emerald-400 font-semibold">Play Knockout →</span>;
                  return <span className="text-xs text-white/50">{ko}/31 knockout picks</span>;
                } catch { return <span className="text-xs text-emerald-400 font-semibold">Play Knockout →</span>; }
              })()}
            </div>
          </Glass>
        </Link>

        {/* NCAA March Madness */}
        <Link to="/wall" className="group h-full" onClick={() => setSport('ncaa')}>
          <Glass className="p-5 h-full flex flex-col border-orange-500/20 bg-gradient-to-br from-orange-500/10 to-red-700/5 hover:border-orange-400/30 transition-all duration-300">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-2xl">🏀</span>
                <span className="text-xs font-bold text-orange-400 bg-orange-500/20 px-2 py-0.5 rounded">SWEET 16</span>
              </div>
              <ChevronRight className="h-4 w-4 text-orange-400 group-hover:translate-x-1 transition-transform" />
            </div>
            <h3 className="text-lg font-bold text-white mb-1">March Madness 2026</h3>
            <p className="text-xs text-white/40 mb-3">Full bracket · MMER analytics · Live scores</p>
            <span className="text-xs text-orange-400 font-semibold mt-auto">View Bracket →</span>
          </Glass>
        </Link>
      </div>

      {/* Welcome header — clean + warm */}
      <div className="flex items-center justify-between py-2">
        <div>
          <h1 className="text-2xl font-bold text-white">
            {displayName ? `Welcome back, ${displayName}` : user?.email ? `Welcome back, ${user.email.split('@')[0]}` : 'Welcome back'}
          </h1>
          <p className="text-sm text-white/30 mt-0.5">Your tournaments at a glance</p>
        </div>
        <Link
          to="/bracket"
          className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 text-white font-bold text-sm transition-all duration-200 flex items-center gap-2 shadow-[0_0_15px_rgba(251,146,60,0.15)] hover:shadow-[0_0_25px_rgba(251,146,60,0.25)] active:scale-95"
        >
          <Target className="h-4 w-4" />
          My Bracket
        </Link>
      </div>

      {/* Quick Stats — gradient cards with glow */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Brackets', value: brackets.length, icon: Trophy, gradient: 'from-orange-500/15 to-orange-600/5', border: 'border-orange-500/15', color: 'text-orange-400', glow: 'shadow-[0_0_12px_rgba(251,146,60,0.1)]' },
          { label: 'Leagues', value: leagues.length, icon: Users, gradient: 'from-blue-500/15 to-blue-600/5', border: 'border-blue-500/15', color: 'text-blue-400', glow: 'shadow-[0_0_12px_rgba(59,130,246,0.1)]' },
          { label: 'Total Picks', value: totalPicks, icon: Target, gradient: 'from-green-500/15 to-green-600/5', border: 'border-green-500/15', color: 'text-green-400', glow: 'shadow-[0_0_12px_rgba(34,197,94,0.1)]' },
          { label: 'Best Score', value: bestScore, icon: bestScore > 0 ? Flame : TrendingUp, gradient: bestScore > 0 ? 'from-orange-500/20 to-red-500/10' : 'from-amber-500/15 to-amber-600/5', border: bestScore > 0 ? 'border-orange-500/25' : 'border-amber-500/15', color: bestScore > 0 ? 'text-orange-400 animate-fire' : 'text-amber-400', glow: bestScore > 0 ? 'shadow-[0_0_18px_rgba(251,146,60,0.15)] border-hot' : 'shadow-[0_0_12px_rgba(245,158,11,0.1)]' },
          { label: 'Your Rank', value: bestRank ? `#${bestRank}` : '—', icon: Trophy, gradient: 'from-purple-500/15 to-purple-600/5', border: 'border-purple-500/15', color: 'text-purple-400', glow: 'shadow-[0_0_12px_rgba(168,85,247,0.1)]' },
        ].map((stat) => (
          <div
            key={stat.label}
            className={clsx(
              'p-4 text-center rounded-2xl backdrop-blur-xl border transition-all duration-300 hover:scale-[1.02]',
              `bg-gradient-to-br ${stat.gradient} ${stat.border} ${stat.glow}`,
            )}
          >
            <stat.icon className={clsx('h-6 w-6 mx-auto mb-2 drop-shadow-lg', stat.color)} />
            <div className="text-3xl font-bold text-white tabular-nums">{stat.value}</div>
            <div className="text-[11px] text-white/30 mt-0.5 font-medium tracking-wide uppercase">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Sim progress bar — with glow */}
      {simStatus && simStatus.gamesCompleted > 0 && (
        <Glass className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-white/50 uppercase tracking-wide">Tournament Progress</span>
            <span className="text-xs text-orange-400 font-bold tabular-nums">
              {simStatus.gamesCompleted}/{simStatus.totalGames} games
            </span>
          </div>
          <div className="h-3 bg-white/[0.06] rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-orange-500 via-amber-400 to-yellow-400 rounded-full transition-all duration-700 shadow-[0_0_10px_rgba(251,146,60,0.3)]"
              style={{ width: `${(simStatus.gamesCompleted / simStatus.totalGames) * 100}%` }}
            />
          </div>
          <div className="text-[10px] text-white/20 mt-1.5 text-center">
            {Math.round((simStatus.gamesCompleted / simStatus.totalGames) * 100)}% complete
            {simStatus.currentRound && ` • Round ${simStatus.currentRound}`}
          </div>
        </Glass>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* My Brackets */}
        <Glass className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-white flex items-center gap-2">
              <Trophy className="h-4 w-4 text-orange-400" />
              My Brackets
            </h2>
            <Link to="/bracket" className="text-[11px] text-orange-400 hover:text-orange-300 flex items-center gap-0.5">
              View all <ChevronRight className="h-3 w-3" />
            </Link>
          </div>

          {brackets.length === 0 ? (
            <div className="text-center py-8">
              <Trophy className="h-8 w-8 text-white/10 mx-auto mb-2" />
              <p className="text-xs text-white/30 mb-3">No brackets yet</p>
              <Link
                to="/bracket"
                className="text-xs text-orange-400 hover:text-orange-300 font-medium"
              >
                Create your first bracket →
              </Link>
            </div>
          ) : (
            <div className="space-y-2">
              {brackets.slice(0, 3).map((b) => (
                <div
                  key={b.id}
                  className="flex items-center justify-between p-3 rounded-xl bg-white/[0.02] border border-white/[0.04] hover:bg-white/[0.06] transition-colors group"
                >
                  <Link to={`/bracket/${b.id}`} className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white group-hover:text-orange-300 transition-colors">
                      {b.name}
                    </div>
                    <div className="text-[10px] text-white/30 mt-0.5">
                      {b.totalPicks}/63 picks • {b.isSubmitted ? '✅ Submitted' : '⏳ In progress'}
                    </div>
                  </Link>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      {b.score != null && b.score > 0 && (
                        <div className="text-sm font-bold text-green-400">{b.score} pts</div>
                      )}
                      {b.rank && (
                        <div className="text-[10px] text-white/20">#{b.rank}</div>
                      )}
                    </div>
                    {!b.isSubmitted && (
                      <button
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!window.confirm(`Delete "${b.name}"? This cannot be undone.`)) return;
                          try {
                            await bracketApi.deleteBracket(b.id);
                            setBrackets(prev => prev.filter(x => x.id !== b.id));
                            toast.success('Bracket deleted');
                          } catch (err: any) {
                            toast.error(err?.message || 'Failed to delete bracket');
                          }
                        }}
                        className="p-1.5 rounded-lg text-white/20 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                        title="Delete bracket"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Glass>

        {/* My Leagues */}
        <Glass className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-white flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-400" />
              My Leagues
            </h2>
            <Link to="/leagues" className="text-[11px] text-orange-400 hover:text-orange-300 flex items-center gap-0.5">
              View all <ChevronRight className="h-3 w-3" />
            </Link>
          </div>

          {leagues.length === 0 ? (
            <div className="text-center py-8">
              <Users className="h-8 w-8 text-white/10 mx-auto mb-2" />
              <p className="text-xs text-white/30 mb-3">No leagues yet</p>
              <Link
                to="/leagues"
                className="text-xs text-orange-400 hover:text-orange-300 font-medium"
              >
                Join or create a league →
              </Link>
            </div>
          ) : (
            <div className="space-y-2">
              {leagues.slice(0, 3).map((l) => (
                <Link
                  key={l.id}
                  to={`/leagues/${l.id}`}
                  className="flex items-center justify-between p-3 rounded-xl bg-white/[0.02] border border-white/[0.04] hover:bg-white/[0.06] transition-colors group"
                >
                  <div>
                    <div className="text-sm font-medium text-white group-hover:text-blue-300 transition-colors">
                      {l.name}
                    </div>
                    <div className="text-[10px] text-white/30 mt-0.5">
                      {l.memberCount} members
                    </div>
                  </div>
                  <div className="text-right">
                    {l.myScore != null && l.myScore > 0 && (
                      <div className="text-sm font-bold text-green-400">{l.myScore} pts</div>
                    )}
                    {l.myRank && (
                      <div className="text-[10px] text-white/20">#{l.myRank}</div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Glass>
      </div>

      {/* Quick Actions — always visible, above the feed */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { to: '/bracket', icon: Target, label: 'Pick Games', gradient: 'from-orange-500/15 to-red-500/5', border: 'border-orange-500/20 hover:border-orange-400/50', iconColor: 'text-orange-400', glow: 'hover:shadow-[0_0_20px_rgba(251,146,60,0.15)]' },
          { to: '/leagues', icon: Users, label: 'Leagues', gradient: 'from-blue-500/15 to-indigo-500/5', border: 'border-blue-500/20 hover:border-blue-400/50', iconColor: 'text-blue-400', glow: 'hover:shadow-[0_0_20px_rgba(59,130,246,0.15)]' },
          { to: '/leaderboard', icon: TrendingUp, label: 'Leaderboard', gradient: 'from-amber-500/15 to-yellow-500/5', border: 'border-amber-500/20 hover:border-amber-400/50', iconColor: 'text-amber-400', glow: 'hover:shadow-[0_0_20px_rgba(245,158,11,0.15)]' },
        ].map((action) => (
          <Link
            key={action.to}
            to={action.to}
            className={clsx(
              'p-4 rounded-2xl bg-gradient-to-br border transition-all duration-300 text-center group hover:scale-[1.03] active:scale-95',
              action.gradient, action.border, action.glow,
            )}
          >
            <action.icon className={clsx('h-6 w-6 mx-auto mb-1.5 group-hover:scale-110 transition-transform duration-300', action.iconColor)} />
            <div className="text-xs font-bold text-white">{action.label}</div>
          </Link>
        ))}
      </div>

      {/* Live Feed */}
      <Glass className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-white flex items-center gap-2">
            <Zap className="h-4 w-4 text-orange-400" />
            Live Feed
            <LivePulse />
          </h2>
          <span className="text-[10px] text-white/20">Updates as games finish</span>
        </div>
        <div className="max-h-[350px] overflow-y-auto">
          <LiveFeed maxEvents={6} />
        </div>
      </Glass>
    </div>
  )
}

export default DashboardPage
